import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';

import GeoLaunchService from 'services/GeoLaunchService';
import { adUrl, adsetUrl } from './adsManager';
import { GeoInfo, GeoLaunchRow } from './geoLaunch';

const STATUS_COLOR: Record<
  string,
  'default' | 'success' | 'error' | 'info' | 'warning'
> = {
  DONE: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
  RUNNING: 'info',
};

interface GeoBatch {
  batchId: string;
  createdAt: string;
  geo: string;
  accountId: string;
  rows: GeoLaunchRow[];
  campaignId: string | null;
  campaignName: string | null;
  campaignCreated: boolean;
  adsetId: string | null;
  adsetName: string | null;
  status: string;
}

function batchStatus(rows: GeoLaunchRow[]): string {
  if (rows.some(r => r.status === 'RUNNING' || r.status === 'PENDING'))
    return 'RUNNING';
  const failed = rows.filter(r => r.status === 'FAILED').length;
  if (!failed) return 'DONE';
  return failed === rows.length ? 'FAILED' : 'PARTIAL';
}

export function groupGeoBatches(rows: GeoLaunchRow[]): GeoBatch[] {
  const byBatch = new Map<string, GeoLaunchRow[]>();
  for (const r of rows)
    byBatch.set(r.batch_id, [...(byBatch.get(r.batch_id) || []), r]);
  return Array.from(byBatch.entries())
    .map(([batchId, rs]) => {
      const first = rs[0];
      return {
        batchId,
        createdAt: rs.map(r => r.created_at).sort()[0],
        geo: first.geo,
        accountId: first.account_id,
        rows: rs,
        campaignId: rs.find(r => r.campaign_id)?.campaign_id ?? null,
        campaignName: rs.find(r => r.campaign_name)?.campaign_name ?? null,
        campaignCreated: rs.some(r => r.campaign_created),
        adsetId: rs.find(r => r.adset_id)?.adset_id ?? null,
        adsetName: rs.find(r => r.adset_name)?.adset_name ?? null,
        status: batchStatus(rs),
      };
    })
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

/** Every geo launch made from this tab, one row per ad set, newest first. */
export default function GeoHistory({
  service,
}: {
  service: GeoLaunchService;
  geos: GeoInfo[];
}) {
  const [rows, setRows] = useState<GeoLaunchRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await service.getLaunches(300));
    } catch (err) {
      const anyErr = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      setError(
        anyErr?.response?.data?.error ||
          anyErr?.message ||
          'Could not load geo launch history.',
      );
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    load();
  }, [load]);

  const batches = useMemo(() => groupGeoBatches(rows || []), [rows]);
  const toggle = (id: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Every ad set launched from Geo Launch. Ads copied into a geo by hand
          are not listed here — they show in the Winners grid instead.
        </Typography>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {rows && !batches.length && (
        <Typography variant="body2" color="text.secondary">
          No geo launches yet.
        </Typography>
      )}
      {batches.length > 0 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>When</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Geo</TableCell>
                <TableCell>Ads</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Ad set</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map(b => {
                const failed = b.rows.filter(r => r.status === 'FAILED').length;
                return (
                  <React.Fragment key={b.batchId}>
                    <TableRow
                      hover
                      onClick={() => toggle(b.batchId)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <IconButton size="small">
                          {open.has(b.batchId) ? (
                            <KeyboardArrowDownIcon />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(b.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{b.rows[0].requested_by || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={b.geo} />
                      </TableCell>
                      <TableCell>{b.rows.length}</TableCell>
                      <TableCell>
                        {b.campaignName || '—'}
                        {b.campaignCreated && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="new · paused"
                            sx={{ ml: 1, height: 18 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {b.adsetId ? (
                          <Link
                            href={adsetUrl(
                              b.accountId,
                              b.adsetId,
                              b.campaignId,
                            )}
                            target="_blank"
                            rel="noopener"
                            onClick={e => e.stopPropagation()}
                          >
                            {b.adsetName}
                          </Link>
                        ) : (
                          b.adsetName || '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATUS_COLOR[b.status] || 'default'}
                          label={
                            failed && b.status !== 'FAILED'
                              ? `${b.status} · ${failed} failed`
                              : b.status
                          }
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{
                          py: 0,
                          borderBottom: open.has(b.batchId)
                            ? undefined
                            : 'none',
                        }}
                      >
                        <Collapse in={open.has(b.batchId)} unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            {b.rows.map(r => (
                              <Stack
                                key={r.launch_id}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ py: 0.5 }}
                              >
                                <Chip
                                  size="small"
                                  color={STATUS_COLOR[r.status] || 'default'}
                                  label={r.status}
                                  sx={{ minWidth: 72 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ minWidth: 240 }}
                                >
                                  {r.meta_ad_id ? (
                                    <Link
                                      href={adUrl(r.account_id, r.meta_ad_id, {
                                        campaignId: r.campaign_id,
                                        adsetId: r.adset_id,
                                      })}
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {r.source_ad_name}
                                    </Link>
                                  ) : (
                                    r.source_ad_name
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  title={r.landing_url || ''}
                                >
                                  {r.landing_url}
                                </Typography>
                                {r.error_message && (
                                  <Typography variant="caption" color="error">
                                    {r.error_message}
                                  </Typography>
                                )}
                              </Stack>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
